// Paper-doll character compositing in the browser: stacks the Player layer
// sheets in z-order and recolours them.
//
// Two recolour passes, both applied to EVERY layer (see recolorFrame):
//
//  1. SKIN. The pack bakes skin-coloured pixels into layers that are not the
//     body sheet — every hair sheet paints the forehead/temple band under the
//     fringe, and the lantern/torch hand sheets paint bare fingers. Those sit
//     ABOVE the body in z-order, so tinting only the `skin` slot left a
//     default-tone band on the forehead of every dark-skinned farmer (and, with
//     a custom hair colour, a *pink* one, because the hue pass grabbed them).
//     The body sheet uses exactly three opaque colours — two skin ramp steps
//     plus the outline — so skin pixels are identifiable by value anywhere they
//     appear. We multiply those, and only those, by the chosen tone, in every
//     layer. Multiply keeps the pack's own two-step ramp intact.
//
//  2. GARMENT. Custom clothing colours used to be an HSL hue ROTATION, which
//     cannot work: a hue rotation is undefined for achromatic art (a grey or
//     white shirt has no hue to rotate, so `Hair_1_Grey` and the black shirt
//     came back byte-identical), and it preserves the SOURCE lightness, so a
//     near-white shoe asked to become dark red returned pale pink. Both are
//     exactly the reported bug. The replacement is a luminance-preserving
//     REMAP: each pixel's lightness is normalised against the layer's own
//     lightness range and replayed as a shading ramp around the TARGET colour's
//     lightness, at the target hue and saturation. The chosen colour therefore
//     always lands (mid-tones come out at the target itself) while the art's
//     internal shading survives as relative steps. Outlines and skin pixels are
//     excluded so the black linework and the baked skin stay put.
import { CHAR_SLOTS, FRAME_W, FRAME_H } from './manifest.generated';

/** Per-slot choice: which layer file (null = empty), and an optional custom colour. */
export interface SlotChoice { file: string | null; color: string | null }
export type Appearance = Record<string, SlotChoice>;

const imgCache = new Map<string, Promise<HTMLImageElement>>();
function loadImg(file: string): Promise<HTMLImageElement> {
  let p = imgCache.get(file);
  if (!p) {
    p = new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      // Don't cache the failure permanently — drop it so a later attempt can retry,
      // and so a missing layer surfaces as a catchable error, not a poisoned cache.
      im.onerror = () => { imgCache.delete(file); rej(new Error('load ' + file)); };
      im.src = '/player/' + file;
    });
    imgCache.set(file, p);
  }
  return p;
}

function rgb2hsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  let h = 0, s = 0; const l = (mx + mn) / 2;
  if (mx !== mn) {
    const d = mx - mn;
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    if (mx === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h, s, l];
}
function hue2rgb(p: number, q: number, t: number) {
  if (t < 0) t += 1; if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}
function hsl2rgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
  return [Math.round(hue2rgb(p, q, h + 1 / 3) * 255), Math.round(hue2rgb(p, q, h) * 255), Math.round(hue2rgb(p, q, h - 1 / 3) * 255)];
}
function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const ctx2d = (c: HTMLCanvasElement) => {
  const x = c.getContext('2d', { willReadFrequently: true })!;
  x.imageSmoothingEnabled = false;
  return x;
};

/** The body sheet's opaque palette: two skin ramp steps. Exported so tests (and
 *  any future slicer check) can assert against the same source of truth. */
export const SKIN_PALETTE: ReadonlyArray<readonly [number, number, number]> = [
  [246, 202, 159], // lit
  [210, 159, 112], // shadow
  // Two more baked flesh values the pack uses OUTSIDE the body sheet, found by
  // scanning every /player/ sheet: the bare-hands sheet paints knuckles with
  // (233,188,149) on rows 20/23 and the Hair_4 sheets paint a forehead strip with
  // (230,156,105) on rows 50/53. Both are >SKIN_TOL away from the two body steps,
  // so a dark-skinned hero kept default-tone hands in the in-game action rows.
  // Nothing recolourable shares them (the only other user is the horse sheet,
  // which is drawn untouched).
  [233, 188, 149], // hands sheet, lit knuckles
  [230, 156, 105], // Hair_4 sheets, baked forehead
];
/** Channel tolerance when matching a pixel to the skin palette. The pack is
 *  index-painted, so the values are exact; a small tolerance only absorbs PNG
 *  re-encodes and keeps us from grabbing unrelated garment browns. */
const SKIN_TOL = 6;
/** Anything this dark is linework, not colour: the pack outlines everything with
 *  (14,7,27). Recolouring it drags the black towards the chosen hue and the
 *  sprite loses its silhouette. */
const OUTLINE_MAX = 32; // (14,7,27) and its two neighbours; a BLACK GARMENT is (39,39,39) and must stay recolourable

function isSkinPx(r: number, g: number, b: number): boolean {
  for (const [sr, sg, sb] of SKIN_PALETTE) {
    if (Math.abs(r - sr) <= SKIN_TOL && Math.abs(g - sg) <= SKIN_TOL && Math.abs(b - sb) <= SKIN_TOL) return true;
  }
  return false;
}
const isOutlinePx = (r: number, g: number, b: number) => Math.max(r, g, b) <= OUTLINE_MAX;

/** How far the remapped shading ramp spreads around the target lightness.
 *  ±0.22 reproduces roughly the contrast the pack paints (a 2–4 step ramp)
 *  without crushing dark targets to black or blowing light ones to white. */
const RAMP_SPREAD = 0.44;

export interface RecolorOpts {
  /** Multiply tone for skin pixels, wherever they appear in this layer. */
  skin?: string | null;
  /** Target colour for the garment/hair pixels of this layer. */
  garment?: string | null;
  /** [lo, hi] lightness range of the WHOLE sheet this frame came from. Supplied by
   *  drawLayer so the ramp is frame-independent (see recolorFrame). */
  range?: readonly [number, number] | null;
}

/** Recolour a decoded frame in place. Exported for tests, which run this over
 *  raw sheet pixels without a DOM canvas stack. */
export function recolorFrame(d: Uint8ClampedArray, opts: RecolorOpts): void {
  const skin = opts.skin ? hexToRgb(opts.skin) : null;
  const garment = opts.garment ? hexToRgb(opts.garment) : null;
  if (!skin && !garment) return;

  // Pass 1: find the lightness range the garment ramp is normalised against.
  //
  // This MUST come from the whole sheet (`opts.range`, computed once per image by
  // sheetRange()), never from the single frame being drawn: different frames of a
  // walk cycle expose different subsets of the art's shades, so a per-frame range
  // remapped the same source pixel to a different output colour frame to frame —
  // trousers visibly strobed between navy and full-bright blue mid-stride. The
  // per-frame scan below survives only as a fallback for callers (tests, one-off
  // buffers) that have no sheet to measure.
  let lo = 1, hi = 0;
  if (garment) {
    if (opts.range) {
      [lo, hi] = opts.range;
    } else {
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 8) continue;
        const r = d[i], g = d[i + 1], b = d[i + 2];
        if (isOutlinePx(r, g, b) || isSkinPx(r, g, b)) continue;
        const l = rgb2hsl(r, g, b)[2];
        if (l < lo) lo = l;
        if (l > hi) hi = l;
      }
    }
  }
  const span = hi - lo;
  const [th, ts, tl] = garment ? rgb2hsl(garment[0], garment[1], garment[2]) : [0, 0, 0];

  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] < 8) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    if (isOutlinePx(r, g, b)) continue;              // keep the linework black
    if (isSkinPx(r, g, b)) {
      if (!skin) continue;
      d[i] = (r * skin[0]) / 255; d[i + 1] = (g * skin[1]) / 255; d[i + 2] = (b * skin[2]) / 255;
      continue;
    }
    if (!garment) continue;
    const l = rgb2hsl(r, g, b)[2];
    // Flat art (a single-colour garment) has no ramp to preserve — hand it the
    // target itself rather than dividing by ~0.
    const t = span > 0.02 ? (l - lo) / span : 0.5;
    const outL = Math.min(0.97, Math.max(0.05, tl - RAMP_SPREAD / 2 + RAMP_SPREAD * t));
    const [nr, ng, nb] = hsl2rgb(th, ts, outL);
    d[i] = nr; d[i + 1] = ng; d[i + 2] = nb;
  }
}

/** Whole-sheet lightness range of the recolourable pixels, memoised per sheet.
 *  Scanning the full sheet is what makes a custom garment colour stable across
 *  every frame; the cache keeps that to one pass per image, not one per frame. */
const sheetRangeCache = new Map<string, readonly [number, number]>();
function sheetRange(img: HTMLImageElement): readonly [number, number] {
  const key = img.src;
  const hit = sheetRangeCache.get(key);
  if (hit) return hit;
  const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const cx = ctx2d(c);
  cx.drawImage(img, 0, 0);
  let lo = 1, hi = 0;
  try {
    const d = cx.getImageData(0, 0, w, h).data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 8) continue;
      const r = d[i], g = d[i + 1], b = d[i + 2];
      if (isOutlinePx(r, g, b) || isSkinPx(r, g, b)) continue;
      const l = rgb2hsl(r, g, b)[2];
      if (l < lo) lo = l;
      if (l > hi) hi = l;
    }
  } catch { /* tainted/zero-size canvas → fall back to the degenerate range */ }
  const range = [lo, hi] as const;
  sheetRangeCache.set(key, range);
  return range;
}

/** Draw one frame of a layer (optionally recoloured) onto the target ctx. */
function drawLayer(ctx: CanvasRenderingContext2D, img: HTMLImageElement, fx: number, fy: number, opts: RecolorOpts) {
  const off = document.createElement('canvas');
  off.width = FRAME_W; off.height = FRAME_H;
  const o = ctx2d(off);
  o.drawImage(img, fx * FRAME_W, fy * FRAME_H, FRAME_W, FRAME_H, 0, 0, FRAME_W, FRAME_H);
  if (opts.skin || opts.garment) {
    const id = o.getImageData(0, 0, FRAME_W, FRAME_H);
    recolorFrame(id.data, opts.garment ? { ...opts, range: sheetRange(img) } : opts);
    o.putImageData(id, 0, 0);
  }
  ctx.drawImage(off, 0, 0);
}

const slotById = Object.fromEntries(CHAR_SLOTS.map((s) => [s.id, s]));
const zOrder = [...CHAR_SLOTS].sort((a, b) => a.z - b.z);

// Bare hands are always drawn (not a creator choice), at z=4 — over the shirt,
// under the hair — and skin-tinted so they match the chosen skin colour.
const HANDS_FILE = 'Hands/Hands_1_Bare.png';
const HANDS_Z = 4;

/** An instrument sheet overlaid on the hero for a given action frame.
 *  `file` is under /player/, `row` is the tool sheet's own row, `behind`
 *  draws it under the hero (for up-facing rows + the horse mount). */
export interface ToolOverlay { file: string; row: number; behind?: boolean }

/** Draw one tool frame at column fx, tool-sheet row.
 *  Hand sheets (lantern / torch) paint bare fingers, so they take the skin tone
 *  too — otherwise holding a lantern swapped in default-tone hands. Everything
 *  else (axe, hoe, horse) is drawn untouched. */
async function drawTool(ctx: CanvasRenderingContext2D, tool: ToolOverlay, fx: number, skin: string | null) {
  const img = await loadImg(tool.file);
  if (skin && tool.file.includes('Hands')) {
    drawLayer(ctx, img, fx, tool.row, { skin });
    return;
  }
  ctx.drawImage(img, fx * FRAME_W, tool.row * FRAME_H, FRAME_W, FRAME_H, 0, 0, FRAME_W, FRAME_H);
}

/** Compose the raw character frame (unscaled), optionally with a tool overlay. */
async function composeBase(app: Appearance, fx = 0, fy = 0, tool?: ToolOverlay): Promise<HTMLCanvasElement> {
  const base = document.createElement('canvas');
  base.width = FRAME_W; base.height = FRAME_H;
  const ctx = ctx2d(base);
  // horse / up-facing tool sits under the hero.
  //
  // The failure is swallowed so one bad overlay cannot blank a whole character —
  // but it is LOGGED, because a silently-missing horse renders as a seated rider
  // floating on nothing, which looks like a game bug rather than a missing file.
  // One tone drives every layer's skin pixels, not just the body sheet.
  const skinTone = app.skin?.color ?? null;
  if (tool?.behind) {
    await drawTool(ctx, tool, fx, skinTone).catch((e) => {
      console.warn(`[character] behind-overlay failed: ${tool.file} row ${tool.row}`, e);
    });
  }
  let handsDrawn = false;
  const drawHands = async () => {
    handsDrawn = true;
    try {
      const img = await loadImg(HANDS_FILE);
      drawLayer(ctx, img, fx, fy, { skin: skinTone });
    } catch { /* bare hands sheet missing → skip */ }
  };
  for (const slot of zOrder) {
    if (!handsDrawn && slot.z > HANDS_Z) await drawHands();
    const ch = app[slot.id];
    if (!ch || !ch.file) continue;
    // A missing cosmetic layer (e.g. an unshipped armour piece) must not abort the
    // whole character — skip it and keep composing. This is what kept the arena from
    // freezing: one un-loadable layer used to reject every hero's compose.
    try {
      const img = await loadImg(ch.file);
      // Skin pixels baked into a garment/hair sheet follow the skin tone; the
      // rest of the layer follows that slot's own custom colour (if any).
      drawLayer(ctx, img, fx, fy, { skin: skinTone, garment: slot.id === 'skin' ? null : ch.color });
    } catch { /* skip this layer */ }
  }
  if (!handsDrawn) await drawHands();
  if (tool && !tool.behind) await drawTool(ctx, tool, fx, skinTone).catch(() => undefined);  // held tool in front of the hero
  return base;
}

/** Compose the full character for a given frame; returns a scaled canvas. */
export async function composeCharacter(app: Appearance, opts?: { fx?: number; fy?: number; scale?: number; tool?: ToolOverlay }): Promise<HTMLCanvasElement> {
  const scale = opts?.scale ?? 8;
  const base = await composeBase(app, opts?.fx ?? 0, opts?.fy ?? 0, opts?.tool);
  const out = document.createElement('canvas');
  out.width = FRAME_W * scale; out.height = FRAME_H * scale;
  const octx = ctx2d(out);
  octx.drawImage(base, 0, 0, out.width, out.height);
  return out;
}

/** Compose the character cropped tight to its pixels — for a HUD portrait. */
export async function composeAvatar(app: Appearance): Promise<HTMLCanvasElement> {
  const base = await composeBase(app);
  const d = ctx2d(base).getImageData(0, 0, FRAME_W, FRAME_H).data;
  let minx = FRAME_W, miny = FRAME_H, maxx = 0, maxy = 0, found = false;
  for (let y = 0; y < FRAME_H; y++) {
    for (let x = 0; x < FRAME_W; x++) {
      if (d[(y * FRAME_W + x) * 4 + 3] > 8) {
        found = true;
        if (x < minx) minx = x; if (x > maxx) maxx = x;
        if (y < miny) miny = y; if (y > maxy) maxy = y;
      }
    }
  }
  if (!found) return base;
  const w = maxx - minx + 1, h = maxy - miny + 1;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  ctx2d(c).drawImage(base, minx, miny, w, h, 0, 0, w, h);
  return c;
}

/** Render a single preset layer thumbnail (for swatches). */
export async function layerThumb(file: string, scale = 3): Promise<HTMLCanvasElement> {
  const img = await loadImg(file);
  const c = document.createElement('canvas');
  c.width = FRAME_W * scale; c.height = FRAME_H * scale;
  const x = ctx2d(c);
  x.drawImage(img, 0, 0, FRAME_W, FRAME_H, 0, 0, c.width, c.height);
  return c;
}

/** Pick a preset whose label matches one of `prefs` (else the first). */
function prefPreset(styleId: string, prefs: string[]): string {
  const st = slotById[styleId].styles[0];
  for (const p of prefs) {
    const m = st.presets.find((x) => x.label.toLowerCase().includes(p));
    if (m) return m.file;
  }
  return st.presets[0].file;
}

export function defaultAppearance(): Appearance {
  // A clearly-visible, cheerful starter outfit (not the all-black first preset).
  return {
    skin: { file: slotById.skin.styles[0].presets[0].file, color: null },
    pants: { file: prefPreset('pants', ['brown', 'blue']), color: null },
    shoes: { file: prefPreset('shoes', ['brown']), color: null },
    shirt: { file: prefPreset('shirt', ['green', 'blue', 'red']), color: null },
    hair: { file: prefPreset('hair', ['brown', 'blonde']), color: null },
    headgear: { file: null, color: null },
  };
}

export function randomAppearance(): Appearance {
  const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];
  const a: Appearance = {};
  for (const s of CHAR_SLOTS) {
    const optionalEmpty = s.optional && Math.random() < (s.id === 'headgear' ? 0.6 : 0.15);
    if (optionalEmpty) { a[s.id] = { file: null, color: null }; continue; }
    const style = pick(s.styles);
    a[s.id] = { file: pick(style.presets).file, color: null };
  }
  a.skin = { file: slotById.skin.styles[0].presets[0].file, color: null };
  return a;
}
